/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * Scoring. Each goal's achieved actual is PRE-FILLED from the tenant's own
 * depletion reports where the metric allows it (item 231 — this screen used to
 * carry a banner reading "automatic metric resolution isn't enabled yet", which
 * contradicted the module's own headline). The rep confirms or overrides, then
 * saves — which computes the payout and writes the immutable incentive_scores
 * row, tagged with how the number was actually obtained.
 *
 * The pre-fill-then-confirm model is deliberate and is the one RESOLVER_DESIGN
 * argued for: a score is a capture taken at a moment, not a live figure, so a
 * resolved value is offered to a human rather than written behind their back.
 */
import { useEffect, useState } from "react";
import { saveScore } from "../utils/incentiveService.js";
import { uploadAttachment, deleteAttachment, attachmentSignedUrl, validateAttachment } from "../utils/attachments.js";
import { computePayout, attainmentPct, describePayout, num } from "../utils/payout.js";
import { METRIC_LABEL, DATA_SOURCE } from "../utils/constants.js";
import { hasAutomaticSource } from "../utils/metricResolver.js";
import { goalProgress } from "../utils/progress.js";
import GoalScope from "./GoalScope.jsx";

function money(n) { return `$${num(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`; }

function EvidenceChip({ att, onDelete, disabled }) {
  const [url, setUrl] = useState(null);
  async function open() {
    const u = url || (await attachmentSignedUrl(att.storage_path));
    setUrl(u);
    if (u) window.open(u, "_blank", "noopener,noreferrer");
  }
  return (
    <span className="it-attach-chip">
      <button className="link" style={{ padding: 0 }} onClick={open}>📎 {att.file_name}</button>
      {!disabled && <button className="it-link-danger" onClick={() => onDelete(att)} aria-label="Remove">✕</button>}
    </span>
  );
}

function GoalScoreCard({ program, goal, score, attachments, readOnly, accountId, onChanged }) {
  const [actual, setActual] = useState(score?.actual_value ?? "");
  const [notes, setNotes] = useState(score?.notes ?? "");
  const [manualPayout, setManualPayout] = useState(score?.payout_earned ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(!!score);

  // Resolution state: what the depletion reports say, and whether the value in
  // the box is still that number (which is what decides resolved_source).
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState(null);   // { value, basis } | null
  const [resolveNote, setResolveNote] = useState("");

  const isCustomMetric = goal.metric_type === "custom";
  const isCustomPayout = goal.payout_type === "custom";
  const preview = isCustomPayout ? null : computePayout(goal, actual);
  const pct = attainmentPct(actual, goal.target_value);
  const evidence = attachments.filter((a) => a.goal_id === goal.id);

  // Pull the actual once per goal. A goal that is already scored keeps its
  // captured number — the resolved figure is shown alongside it, never over it.
  useEffect(() => {
    let alive = true;
    // Ask the resolver about the GOAL, not about the stored data_source column:
    // goals freeze when a program leaves draft, so every goal written before the
    // resolver shipped is stamped 'manual_upload' forever and would never
    // resolve if we gated on the column. The column is a record of intent; the
    // resolver is the authority on what it can actually pull.
    if (!hasAutomaticSource(goal)) { setResolveNote(""); return () => { alive = false; }; }
    if (!accountId) return () => { alive = false; };  // still resolving the tenant
    setResolving(true);
    goalProgress({ program, goal, score: null, accountId })
      .then((p) => {
        if (!alive) return;
        if (p.source === "system") {
          setResolved({ value: p.actual, basis: p.basis });
          setResolveNote("");
          // Only pre-fill an unscored goal; never overwrite a captured number.
          if (!score) { setActual(p.actual); setSaved(false); }
        } else {
          setResolved(null);
          setResolveNote(p.reason);
        }
      })
      .catch(() => { if (alive) setResolveNote("Couldn't read your depletion reports just now."); })
      .finally(() => { if (alive) setResolving(false); });
    return () => { alive = false; };
    // Resolve once per goal — re-running on every keystroke would fight the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal.id, accountId]);

  const matchesResolved = resolved != null && num(actual) === num(resolved.value);

  async function save() {
    setBusy(true); setErr(null);
    try {
      await saveScore(program.id, goal, {
        actual, notes,
        payout: isCustomPayout ? manualPayout : undefined,
        // The rep accepted the resolved figure unchanged → the record says the
        // system produced it. Anything else is their number, and says so.
        resolvedSource: matchesResolved ? DATA_SOURCE.SYSTEM : DATA_SOURCE.MANUAL,
      });
      setSaved(true);
      onChanged?.();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function pick(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const bad = validateAttachment(file);
    if (bad) { setErr(bad); return; }
    setBusy(true); setErr(null);
    try {
      await uploadAttachment(program.id, file, { kind: "score_evidence", goalId: goal.id });
      onChanged?.();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function removeEvidence(att) {
    setBusy(true); setErr(null);
    try { await deleteAttachment(att); onChanged?.(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const unit = goal.unit_label ? ` ${goal.unit_label}` : "";
  return (
    <div className="it-score-goal">
      <div className="it-goal-head">
        <strong>{goal.description || METRIC_LABEL[goal.metric_type] || "Goal"}</strong>
        {saved && <span className="caption">Scored</span>}
      </div>
      <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
        {isCustomMetric
          ? <>Custom goal{goal.custom_text ? <> · <span style={{ whiteSpace: "pre-wrap" }}>{goal.custom_text}</span></> : null}</>
          : <>{METRIC_LABEL[goal.metric_type]} · Target {num(goal.target_value).toLocaleString()}{unit}</>}
        {" · "}{describePayout(goal)}
      </div>
      <GoalScope goal={goal} />

      {/* Provenance for the number in the box — the honest replacement for the
          old blanket "automatic resolution isn't enabled" banner. */}
      {resolving && <p className="it-source" style={{ marginTop: 10 }}>Reading your depletion reports…</p>}
      {!resolving && resolved && (
        <p className={`it-source ${matchesResolved ? "auto" : "override"}`}>
          {matchesResolved
            ? <>{resolved.basis} — {num(resolved.value).toLocaleString()}{unit}.</>
            : <>Your depletion reports say {num(resolved.value).toLocaleString()}{unit}. You've entered a different number, which will be recorded as a manual figure.{" "}
                <button type="button" className="link" style={{ padding: 0 }}
                        onClick={() => { setActual(resolved.value); setSaved(false); }}>Use the reported figure</button></>}
        </p>
      )}
      {!resolving && !resolved && resolveNote && <p className="it-source">{resolveNote}</p>}

      <div className="it-score-grid">
        <label>
          {isCustomMetric ? "Result achieved" : `Actual achieved${unit ? ` (${goal.unit_label})` : ""}`}
          <input type="number" inputMode="decimal" value={actual} disabled={readOnly || busy}
                 onChange={(e) => { setActual(e.target.value); setSaved(false); }} placeholder="0" />
        </label>
        {isCustomMetric ? (
          <div className="it-stat">
            &nbsp;
            <div className="v muted" style={{ fontSize: 13, fontWeight: 500 }}>No fixed target</div>
          </div>
        ) : (
          <div className="it-stat">
            Attainment
            <div className={`v ${pct >= 100 ? "met" : "miss"}`}>{pct.toFixed(0)}%</div>
          </div>
        )}
        {isCustomPayout ? (
          <label>
            Payout earned ($)
            <input type="number" inputMode="decimal" value={manualPayout} disabled={readOnly || busy}
                   onChange={(e) => { setManualPayout(e.target.value); setSaved(false); }} placeholder="0" />
          </label>
        ) : (
          <div className="it-stat">
            Payout {saved ? "earned" : "(preview)"}
            <div className="v">{money(saved ? (score?.payout_earned ?? preview) : preview)}</div>
          </div>
        )}
      </div>

      <label style={{ display: "block", marginTop: 12, fontSize: 13 }}>
        Scoring notes
        <textarea rows={2} value={notes} disabled={readOnly || busy}
                  onChange={(e) => { setNotes(e.target.value); setSaved(false); }}
                  placeholder="How was this measured? Source report, caveats…" />
      </label>

      <div className="it-attach">
        {evidence.map((a) => <EvidenceChip key={a.id} att={a} onDelete={removeEvidence} disabled={readOnly} />)}
      </div>

      {err && <p className="error" style={{ fontSize: 13 }}>{err}</p>}

      {!readOnly && (
        <div style={{ display: "flex", gap: 10, marginTop: 12, alignItems: "center" }}>
          <button className="btn" onClick={save} disabled={busy}>{saved ? "Update score" : "Save score"}</button>
          <label className="secondary" style={{ cursor: "pointer", margin: 0 }}>
            Attach report
            <input type="file" style={{ display: "none" }} onChange={pick} disabled={busy} />
          </label>
        </div>
      )}
      {readOnly && score?.resolved_source && (
        <p className="it-source" style={{ marginTop: 10 }}>
          {score.resolved_source === DATA_SOURCE.SYSTEM
            ? "This figure came from your depletion reports and was confirmed at scoring."
            : "This figure was entered by hand at scoring."}
        </p>
      )}
    </div>
  );
}

export default function ScoringPanel({ program, goals, scores, attachments, readOnly, accountId, onChanged }) {
  const scoreByGoal = new Map(scores.map((s) => [s.goal_id, s]));
  const totalEarned = scores.reduce((sum, s) => sum + num(s.payout_earned), 0);
  const autoCount = goals.filter(hasAutomaticSource).length;

  return (
    <div>
      {!readOnly && goals.length > 0 && (
        <div className="it-note">
          {autoCount === goals.length
            ? "Every goal here is filled in from your depletion reports. Check each number, attach anything the distributor sent, and save."
            : autoCount > 0
              ? `${autoCount} of ${goals.length} goals fill in from your depletion reports. The rest are scored from your own report — each one says which, and why.`
              : "These goals are scored from your own reports — custom and SKU-scoped goals have no per-account depletion figure to pull. Enter each actual and attach the report behind it."}
        </div>
      )}
      {goals.length === 0 && <p className="muted">This program has no goals to score.</p>}
      {goals.map((g) => (
        <GoalScoreCard
          key={g.id} program={program} goal={g}
          score={scoreByGoal.get(g.id)}
          attachments={attachments}
          readOnly={readOnly}
          accountId={accountId}
          onChanged={onChanged}
        />
      ))}
      {goals.length > 0 && (
        <div className="section-row" style={{ marginTop: 8 }}>
          <h2 style={{ margin: 0 }}>Total payout earned</h2>
          <strong style={{ fontSize: 20 }}>{money(totalEarned)}</strong>
        </div>
      )}
    </div>
  );
}
